import { Option } from "@/common/types/option";
import { getNestedValue } from "../utils/getNestedValue";

type NestedKeyOf<T> = (
  T extends Array<infer U> ?
  NestedKeyOf<U> :
  (
    {
      [K in keyof T & (string | number)]: T[K] extends object
      ? [K] | [K, ...NestedKeyOf<T[K]>]
      : [K]
    }[keyof T & (string | number)]
  )
) & (string | number)[];

type MapToOptionsParamsType<T, N extends boolean, M extends boolean> = {
  labelFieldName: N extends true ? NestedKeyOf<T> : keyof T | (keyof T)[];
  valueFieldName: M extends true ? NestedKeyOf<T> : keyof T;
  nestedLabelField?: N;
  nestedValueField?: M;
  returnItem?: boolean;
}


export class Mapper {
  static default<T>() {
    return (data: T) => data
  }

  static mapToOptions<T, const N extends boolean = false, const M extends boolean = false>({
    labelFieldName,
    valueFieldName,
    nestedLabelField = false as N,
    nestedValueField = false as M,
    returnItem = false,
  }: MapToOptionsParamsType<T, N, M>) {
    return (data: T[]) =>
      data.map(item => {
        const option = {
          label: '' as any,
          value: '' as any,
        }

        if (nestedLabelField && labelFieldName instanceof Array) {
          option.label = getNestedValue(item, labelFieldName as (string | keyof T)[]);
        } else if (labelFieldName instanceof Array) {
          option.label = (labelFieldName as (keyof T)[]).map(fieldName => item[fieldName]).join(' ');
        } else {
          option.label = item[labelFieldName as keyof T]
        }

        if (nestedValueField && valueFieldName instanceof Array) {
          option.value = getNestedValue(item, valueFieldName as (string | keyof T)[]);
        } else {
          option.value = item[valueFieldName as keyof T];
        }

        if (returnItem) {
          return { ...option, item }
        }

        return option;
      }) as Option[]
  }

  static mapToOptionsWithItem<T, const N extends boolean = false, const M extends boolean = false>({
    labelFieldName,
    valueFieldName,
    nestedLabelField = false as N,
    nestedValueField = false as M,
  }: MapToOptionsParamsType<T, N, M>) {
    return this.mapToOptions({ labelFieldName, valueFieldName, nestedLabelField, nestedValueField, returnItem: true });
  }

  static mapWithTransformer<T, R>({ transformer }: { transformer: (data: T) => R }) {
    return (data: T) => transformer(data)
  }
}
